import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

const getEduLabel = (value) => {
  switch (value) {
    case "E0":
      return "All Education Categories";
    case "E1":
      return "Less than high school";
    case "E2":
      return "High school or equivalent";
    case "E3":
      return "Some college / Associate degree";
    case "E4":
      return "Bachelor's or advanced degree";
    case "E5":
      return "Educational attainment not available";
    default:
      return "Unknown";
  }
};

export default function EducationGrowthChart({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p className="text-center">No data available</p>;
  }

  /* =========================
     SUMMARY CALCULATIONS
  ========================== */

  const firstTotal = data[0].total_2018;
  const lastTotal = data[data.length - 1].total_2024;

  const overallGrowth = (((lastTotal - firstTotal) / firstTotal) * 100).toFixed(
    1,
  );

  const highestGrowth = data.reduce(
    (max, item) =>
      item.growth_percent > max.value
        ? {
            label: getEduLabel(item.education_label),
            value: item.growth_percent,
          }
        : max,
    { label: "", value: -Infinity },
  );

  /* =========================
     CHART DATA
  ========================== */

  const chartData = {
    labels: data.map((d) => getEduLabel(d.education_label)),
    datasets: [
      {
        label: "2018 Total",
        data: data.map((d) => d.total_2018),
        backgroundColor: "#ec4899",
        borderRadius: 6,
      },
      {
        label: "2024 Total",
        data: data.map((d) => d.total_2024),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Education Workforce Growth (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Education Level" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Total Workforce" },
      },
    },
  };

  /* =========================
     PDF DOWNLOAD (Multi Page)
  ========================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      pdf.line(10, 260, 200, 260);
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, 275);
      pdf.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdf.text("using", 112, 275);
      pdf.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("education_growth.pdf");
    });
  };

  /* =========================
     UI
  ========================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Education Workforce Growth (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-xl font-bold text-blue-800">+{overallGrowth}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Growth Category
            </p>
            <p className="text-md font-bold text-yellow-800">
              {highestGrowth.label}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              2024 Total Workforce
            </p>
            <p className="text-xl font-bold text-green-800">
              {lastTotal.toLocaleString()} 👷
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education</th>
                  <th className="p-2">2018</th>
                  <th className="p-2">2024</th>
                  <th className="p-2">Growth %</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-100">
                    <td className="p-2">{getEduLabel(row.education_label)}</td>
                    <td className="p-2">{row.total_2018.toLocaleString()}</td>
                    <td className="p-2">{row.total_2024.toLocaleString()}</td>
                    <td
                      className={`p-2 font-bold ${
                        row.growth_percent > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {row.growth_percent}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
